/**
 * Kickard PHASE 5.0A — PASS 1C — Shared online-action hook.
 *
 * Extracts the proven `createRoom` / `joinRoom` friend-match actions
 * that previously lived inline in `OnlineLobbyScreen.jsx` so BOTH the
 * legacy Online lobby route and the new Play Hub can invoke the same
 * logic. This is a pure refactor — the HTTP endpoints, payload shape,
 * error semantics, session persistence and telemetry events are all
 * kept identical to the working lobby implementation.
 *
 * NOTHING about matchmaking rules, room lifecycle, session tokens,
 * WebSocket handshake, or rematch policy is touched. Only where the
 * function BODY lives has changed.
 *
 * Callers:
 *   - OnlineLobbyScreen (legacy /online route)
 *   - PlayHubScreen (new /play-hub route)
 */
import { useCallback, useState } from 'react';
import { ensureGuest, loadGuest } from '@/lib/guestIdentity';
import { saveOnlineSession, loadOnlineSessionForRoom } from '@/net/onlineSession';
import { getSessionId, track, EVT } from '@/lib/telemetry';

const BACKEND = process.env.REACT_APP_BACKEND_URL;

/**
 * useOnlineActions
 *
 * Returns:
 *   {
 *     busy:        boolean — is a request in flight?
 *     error:       string|null — last user-facing error, or null.
 *     createRoom:  async () => session|null,
 *     joinRoom:    async (code: string) => session|null,
 *     clearError:  () => void,
 *   }
 *
 * `session` shape (identical to what `OnlineLobbyScreen` handed to
 * `onEnterMatch` before this extraction):
 *   { roomCode, playerId, token, name, opponentName? }
 *
 * All errors are caught and surfaced via `error`. Callers may inspect
 * `error` or set their own `onError` after `await`.
 */
export function useOnlineActions() {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);

  const clearError = useCallback(() => setError(null), []);

  const _getName = () => {
    const g = ensureGuest();
    return (g && g.displayName) || 'Player';
  };

  const createRoom = useCallback(async () => {
    if (busy) return null;
    setBusy(true);
    setError(null);
    try {
      const guest = loadGuest();
      const r = await fetch(`${BACKEND}/api/rooms/create`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: _getName(),
          guestId: guest && guest.guestId,
          sessionId: getSessionId(),
        }),
      });
      if (!r.ok) {
        const text = await r.text();
        throw new Error(`Create failed (${r.status}): ${text.slice(0, 200)}`);
      }
      const data = await r.json();
      const session = {
        roomCode: data.roomCode,
        playerId: data.playerId,
        token: data.token,
        name: data.name,
      };
      // Persist IMMEDIATELY so a backgrounded mobile that loses its
      // WS mid-handshake can still re-enter the same room/slot.
      saveOnlineSession(session);
      return session;
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error('[useOnlineActions] create error', e);
      const msg = String(e.message || e);
      setError(msg);
      try {
        track(EVT.FRIEND_ROOM_CREATE_FAILED, {
          metadata: { message: msg.slice(0, 200) },
        });
      } catch (_) { /* ignore */ }
      return null;
    } finally {
      setBusy(false);
    }
  }, [busy]);

  const joinRoom = useCallback(async (rawCode) => {
    if (busy) return null;
    setBusy(true);
    setError(null);
    try {
      const code = (rawCode || '').trim().toUpperCase();
      if (code.length !== 6) throw new Error('Room code must be 6 characters');

      // If we already hold a valid token for THIS room, reuse it
      // instead of POSTing /join — the server will see the same token
      // and re-attach the same player. Avoids the false "room full".
      const saved = loadOnlineSessionForRoom(code);
      if (saved) {
        // eslint-disable-next-line no-console
        console.log('[useOnlineActions] reusing saved session for room', code, {
          playerId: saved.playerId,
        });
        return {
          roomCode: saved.roomCode,
          playerId: saved.playerId,
          token: saved.token,
          name: saved.name,
        };
      }

      const guest = loadGuest();
      const r = await fetch(`${BACKEND}/api/rooms/${code}/join`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: _getName(),
          guestId: guest && guest.guestId,
          sessionId: getSessionId(),
        }),
      });
      if (r.status === 404) throw new Error('Room not found. Check the code.');
      if (r.status === 409) throw new Error('Room is full.');
      if (!r.ok) {
        const text = await r.text();
        throw new Error(`Join failed (${r.status}): ${text.slice(0, 200)}`);
      }
      const data = await r.json();
      const session = {
        roomCode: data.roomCode,
        playerId: data.playerId,
        token: data.token,
        name: data.name,
      };
      saveOnlineSession(session);
      return session;
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error('[useOnlineActions] join error', e);
      const msg = String(e.message || e);
      setError(msg);
      try {
        track(EVT.FRIEND_ROOM_JOIN_FAILED, {
          metadata: {
            message: msg.slice(0, 200),
            code: (rawCode || '').toUpperCase(),
          },
        });
      } catch (_) { /* ignore */ }
      return null;
    } finally {
      setBusy(false);
    }
  }, [busy]);

  return { busy, error, createRoom, joinRoom, clearError };
}
